import React, { useEffect } from 'react';
import moment from 'moment';
import 'moment/locale/zh-hk';
import { useTranslation } from 'react-i18next';
import localization, { LanguageEnum, LocaleEnum } from './localization.service';

export interface IPageContext {
    lang?: string;
    originalPath?: string;
    localizedPath?: string;
    isOnlyVersion?: boolean;
    languageCode?: LanguageEnum;
}

interface IPageContextProvider {
    value: IPageContext;
    children: any;
}

const PageContext = React.createContext<IPageContext>({});

export const PageContextProvider = ({ value, children }: IPageContextProvider) => {
    const { i18n } = useTranslation();

    const localeCookie = localization.getLocaleCookie();
    if (value.isOnlyVersion && localeCookie) {
        value.lang = localeCookie;
    }
    let momentLocale: string = 'en';
    switch (value.lang) {
        case LocaleEnum.enSG:
        case LocaleEnum.enMY:
        case LocaleEnum.enUS:
        case LocaleEnum.enGB:
            i18n.changeLanguage(LanguageEnum.en);
            break;
        case LocaleEnum.zhHK:
            i18n.changeLanguage(LanguageEnum.tc);
            momentLocale = 'zh-hk';
            break;
        case LocaleEnum.enHK:
        default:
            value.lang = LocaleEnum.enHK;
            i18n.changeLanguage(LanguageEnum.en);
            break;
    }

    moment.locale(momentLocale);

    // Localized pages own their locale via the URL, so keep the cookie in step with the site being browsed.
    // Single-version pages (blog, charity, help center) read this cookie to decide which locale to present.
    useEffect(() => {
        if (!value.isOnlyVersion && value.lang && localization.getLocaleCookie() !== value.lang) {
            localization.setLocaleCookie(value.lang);
        }
    }, [value.lang, value.isOnlyVersion]);

    return <PageContext.Provider value={value}>{children}</PageContext.Provider>;
};

export const usePageContext = () => React.useContext<IPageContext>(PageContext);
